import React from "react";
import { ClipboardList, Loader2 } from "lucide-react";
import {
  UPDATE_OPTIONS,
  WORK_UPDATE_LIMITS,
} from "../../constants/projectConstants";
import { useWorkUpdate } from "../../hooks/useWorkUpdate";
import Modal from "@/components/ui/Modal";
import { FormField, Input, Select, Textarea } from "@/components/ui/FormComponents";
import { Plus } from "lucide-react";

const { TITLE_MIN, TITLE_MAX, DESC_MAX } = WORK_UPDATE_LIMITS;

export const AddWorkUpdateModal = ({ isOpen, onClose, task, onSuccess }) => {
  const {
    register,
    handleSubmit,
    onSubmit,
    errors,
    watch,
    statusVal,
    descLen,
    progressVal,
    submitting,
    formError,
    setFormError,
  } = useWorkUpdate({ isOpen, onClose, task, onSuccess });

  if (!isOpen || !task) return null;

  const modalTitle = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex flex-col min-w-0">
        <h2 className="text-[16px] font-semibold text-slate-800 tracking-tight leading-none">
          Add Work Update
        </h2>
        {/* <span className="text-[11px] font-bold text-blue-400 tracking-tight mt-1.5 truncate">
          {task.taskName}
        </span> */}
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-end gap-3 w-full">
      <button
        type="button"
        onClick={onClose}
        className="px-6 py-2.5 bg-white border border-slate-200 text-slate-500 rounded-md cursor-pointer text-[12px] font-semibold tracking-wider hover:bg-slate-50 transition-all shadow-sm"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit(onSubmit, () => {
          setFormError("Please check the form for errors and fill all required fields.");
        })}
        disabled={submitting}
        className="px-8 py-2.5 bg-[#f97316] hover:bg-[#ea580c] cursor-pointer text-white rounded-md text-[12px] font-semibold tracking-wider transition-all disabled:opacity-50 disabled:grayscale flex items-center gap-3 shadow-md shadow-blue-200"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Update"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={modalFooter}
      width="max-w-[560px]"
    >
      <form
        onSubmit={handleSubmit(onSubmit, () => {
          setFormError("Please check the form for errors and fill all required fields.");
        })}
        className="flex flex-col gap-5 font-poppins"
      >
        {formError && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-[11px] font-bold text-rose-700 animate-in fade-in slide-in-from-top-1">
            {formError}
          </div>
        )}

        <FormField label="Update status" error={errors.updateStatus} required>
          <Select 
            id="work-update-status" 
            {...register("updateStatus")}
            className="text-[13px] font-medium"
          >
            <option value="" disabled>Select status...</option>
            {UPDATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Update title" error={errors.title} required>
          <Input
            id="work-update-title"
            {...register("title")}
            placeholder="e.g. Panels mounted on north side"
            autoComplete="off"
            className="text-[13px] font-medium"
          />
        </FormField>

        <FormField label="Description" error={errors.description}>
          <Textarea
            id="work-update-description"
            {...register("description")}
            placeholder="Describe what was done…"
            className="min-h-[100px] text-[13px] font-medium leading-relaxed"
            maxLength={DESC_MAX}
          />
        </FormField>

        <FormField 
          label="Progress" 
          error={errors.progress} 
          required 
        >
          <div className="flex flex-col gap-2">
            <input
              id="work-update-progress"
              type="range"
              min={0}
              max={100}
              value={progressVal}
              onChange={(e) => setValue('progress', Number(e.target.value), { shouldValidate: true })}
              disabled={statusVal === "COMPLETED"}
              className={`w-full h-1.5 rounded-full appearance-none cursor-pointer transition-all accent-[#2b448f] ${
                statusVal === "COMPLETED" ? "bg-[#2b448f]" : "bg-slate-100"
              }`}
            />
            <div className="flex justify-between text-[10px] font-bold text-slate-400 mt-1">
              <span>0%</span>
              <span className="text-[#2b448f] font-bold">
                {progressVal}% Complete
              </span>
              <span>100%</span>
            </div>
          </div>
        </FormField>
      </form>
    </Modal>
  );
};
